<template>
  <div>
    <b-card no-body>
      <div class="supervision-toolbar">
        <div class="supervision-chips">
          <button
            v-for="f in filterOptions"
            :key="f.key"
            type="button"
            class="chip"
            :class="[`chip--${f.key}`, { 'chip--active': filter === f.key }]"
            @click="filter = f.key"
          >
            <span class="chip__dot" />
            <span class="chip__label">{{ f.label }}</span>
            <span class="chip__count">{{ f.count }}</span>
          </button>
        </div>

        <div class="supervision-search">
          <feather-icon icon="SearchIcon" size="16" class="supervision-search__icon" />
          <input
            v-model="search"
            type="search"
            class="form-control"
            :placeholder="$t('supervision.searchPlaceholder')"
          />
        </div>

        <small class="supervision-updated text-muted">
          <span class="live-dot" :class="{ 'live-dot--off': !live }" />
          {{ live ? $t('supervision.live') : $t('supervision.reconnecting') }}
          · {{ $t('supervision.updatedAt', { time: updatedAtLabel }) }}
        </small>
      </div>

      <div v-if="attention.length" class="attention">
        <div class="attention__title">
          <feather-icon icon="BellIcon" size="16" class="mr-50" />
          {{ $t('supervision.attention.title', { count: attention.length }) }}
        </div>
        <ul class="attention__list">
          <li
            v-for="item in attention"
            :key="item.key"
            class="attention__item"
            :class="`attention__item--${item.level}`"
            @click="search = item.name"
          >
            <span class="attention__dot" />
            <strong>{{ item.name }}</strong>
            <span>{{ item.text }}</span>
          </li>
        </ul>
      </div>

      <div class="table-responsive">
        <table class="table table-hover mb-0 supervision-table">
          <thead>
            <tr>
              <th>{{ $t('supervision.columns.user') }}</th>
              <th>{{ $t('supervision.columns.session') }}</th>
              <th>{{ $t('supervision.columns.status') }}</th>
              <th>{{ $t('supervision.columns.dangerOccurrences') }}</th>
              <th>{{ $t('supervision.columns.pauseType') }}</th>
              <th />
            </tr>
          </thead>
          <tbody>
            <tr v-if="!loading && !visibleRows.length">
              <td colspan="6" class="text-center text-muted py-2">
                {{ $t('supervision.empty') }}
              </td>
            </tr>
            <tr v-for="row in visibleRows" :key="row.user_id" :class="rowClass(row)">
              <td class="align-middle">
                <div class="font-weight-bold">{{ row.name }}</div>
                <small class="text-muted">{{ row.login }} · {{ row.node_name }}</small>
              </td>
              <td class="align-middle text-nowrap">
                <div>
                  <small class="text-muted">{{ $t('supervision.columns.lastAction') }}</small>
                  {{ timeOnly(row.last_action_at) }}
                  <small class="text-muted">({{ agoLabel(row.lastActionAgo) }})</small>
                </div>
                <div>
                  <small class="text-muted">{{ $t('supervision.columns.loggedInAt') }}</small>
                  <small>{{ dateTime(row.logged_in_at) }}</small>
                </div>
              </td>
              <td class="align-middle">
                <b-badge :variant="statusVariant(row)">
                  {{ $t(`supervision.status.${row.effectiveStatus}`) }}
                </b-badge>
              </td>
              <td class="align-middle">
                <div
                  v-for="o in dangerShown(row)"
                  :key="o.id"
                  class="supervision-danger"
                >
                  <b-badge variant="danger">{{ o.protocol }}</b-badge>
                  <small class="tabular text-danger font-weight-bold">+{{ clock(o.over) }}</small>
                  <small class="text-muted">{{ $t(`supervision.dangerStep.${o.step}`) }}</small>
                </div>
                <a
                  v-if="row.danger.length > DANGER_PREVIEW"
                  href="#"
                  class="supervision-danger__more"
                  @click.prevent="toggleDanger(row.user_id)"
                >
                  {{
                    expanded[row.user_id]
                      ? $t('supervision.dangerLess')
                      : $t('supervision.dangerMore', { count: row.danger.length - DANGER_PREVIEW })
                  }}
                </a>
                <span v-if="!row.danger.length" class="text-muted">—</span>
              </td>
              <td class="align-middle supervision-pause">
                <template v-if="row.pause">
                  <div class="font-weight-bold">{{ row.pause.type_name }}</div>
                  <div class="supervision-pause__bar" :class="{ 'supervision-pause__bar--over': row.overdue }">
                    <span :style="{ width: pausePercent(row) + '%' }" />
                  </div>
                  <small class="tabular" :class="row.overdue ? 'text-danger font-weight-bold' : 'text-muted'">
                    {{ clock(row.elapsed) }} ·
                    {{
                      row.overdue
                        ? $t('supervision.pauseInfo.overdue', { time: clock(-row.remaining) })
                        : $t('supervision.pauseInfo.remaining', { time: clock(row.remaining) })
                    }}
                  </small>
                  <div>
                    <small class="text-muted">{{ $t('supervision.pauseInfo.started', { time: timeOnly(row.pause.started_at) }) }}</small>
                  </div>
                </template>
                <span v-else class="text-muted">—</span>
              </td>
              <td class="align-middle text-right">
                <b-button
                  v-if="row.pause && $hasAbility('supervisao.encerrar_pausa')"
                  size="sm"
                  variant="outline-primary"
                  :disabled="ending === row.user_id"
                  @click="endPause(row)"
                >
                  {{ $t('supervision.endPause') }}
                </b-button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </b-card>
  </div>
</template>

<script>
import moment from "moment";
import ApiErrorMixin from "@/mixins/ApiErrorMixin";
import { serverNow } from "@/libs/serverTime";
import { formatClock, formatSeconds } from "@/utils/duration";

const EVENT = ".SupervisionChanged";
const REFRESH_MS = 60000;
const DANGER_PREVIEW = 2;

export default {
  mixins: [ApiErrorMixin],
  data() {
    return {
      raw: [],
      loading: true,
      ending: null,
      search: "",
      filter: "all",
      now: serverNow(),
      updatedAt: null,
      tickTimer: null,
      refreshTimer: null,
      reloadDebounce: null,
      channelName: null,
      listener: null,
      baseTitle: document.title,
      DANGER_PREVIEW,
      expanded: {},
      live: true,
      connection: null,
    };
  },
  computed: {
    rows() {
      return this.raw.map((r) => {
        const lastActionAgo = Math.max(0, Math.floor((this.now - Date.parse(r.last_action_at)) / 1000));
        const pause = r.pause;
        const elapsed = pause ? Math.max(0, Math.floor((this.now - Date.parse(pause.started_at)) / 1000)) : 0;
        // ceil: decorrido + restante = tempo máximo da pausa, sem perder 1s.
        const remaining = pause ? Math.ceil((Date.parse(pause.expected_end_at) - this.now) / 1000) : 0;

        let effectiveStatus = "operating";
        if (pause) effectiveStatus = "paused";
        else if (lastActionAgo >= r.idle_after_minutes * 60) effectiveStatus = "idle";

        // Ocorrências que o operador assumiu e que já passaram do tempo de perigo da etapa.
        const danger = (r.occurrences || [])
          .map((o) => ({
            ...o,
            over: Math.floor((this.now - Date.parse(o.step_since)) / 1000) - o.danger_seconds,
          }))
          .filter((o) => o.over > 0)
          .sort((a, b) => b.over - a.over);

        return {
          ...r,
          lastActionAgo,
          elapsed,
          remaining,
          overdue: !!pause && remaining < 0,
          effectiveStatus,
          danger,
        };
      });
    },
    counts() {
      const rows = this.rows;
      return {
        all: rows.length,
        operating: rows.filter((r) => r.effectiveStatus === "operating").length,
        idle: rows.filter((r) => r.effectiveStatus === "idle").length,
        paused: rows.filter((r) => r.effectiveStatus === "paused").length,
        overdue: rows.filter((r) => r.overdue).length,
        danger: rows.filter((r) => r.danger.length).length,
      };
    },
    // Quem precisa de acompanhamento agora: pausa estourada (vermelho) e ocioso (âmbar).
    attention() {
      const items = [];

      this.rows.forEach((r) => {
        if (r.overdue) {
          items.push({
            key: `overdue-${r.user_id}`,
            level: "danger",
            name: r.name,
            text: this.$t("supervision.attention.overdue", {
              pause: r.pause.type_name,
              time: formatClock(-r.remaining),
            }),
          });
        } else if (r.effectiveStatus === "idle") {
          items.push({
            key: `idle-${r.user_id}`,
            level: "warning",
            name: r.name,
            text: this.$t("supervision.attention.idle", { time: formatSeconds(r.lastActionAgo) }),
          });
        }

        if (r.danger.length) {
          items.push({
            key: `danger-${r.user_id}`,
            level: "danger",
            name: r.name,
            text: this.$t("supervision.attention.danger", { count: r.danger.length, protocol: r.danger[0].protocol, time: formatClock(r.danger[0].over) }),
          });
        }
      });

      return items.sort((a, b) => Number(b.level === "danger") - Number(a.level === "danger"));
    },
    filterOptions() {
      return [
        { key: "all", variant: "primary", label: this.$t("supervision.filters.all"), count: this.counts.all },
        { key: "operating", variant: "success", label: this.$t("supervision.filters.operating"), count: this.counts.operating },
        { key: "idle", variant: "warning", label: this.$t("supervision.filters.idle"), count: this.counts.idle },
        { key: "danger", variant: "danger", label: this.$t("supervision.filters.danger"), count: this.counts.danger },
        { key: "paused", variant: "info", label: this.$t("supervision.filters.paused"), count: this.counts.paused },
        { key: "overdue", variant: "danger", label: this.$t("supervision.filters.overdue"), count: this.counts.overdue },
      ];
    },
    visibleRows() {
      const term = this.search.trim().toLowerCase();

      return this.rows
        .filter((r) => {
          if (this.filter === "overdue") return r.overdue;
          if (this.filter === "danger") return r.danger.length > 0;
          if (this.filter !== "all") return r.effectiveStatus === this.filter;
          return true;
        })
        .filter((r) => !term || `${r.name} ${r.login}`.toLowerCase().includes(term))
        .sort((a, b) => this.severity(b) - this.severity(a) || (a.name || "").localeCompare(b.name || ""));
    },
    updatedAtLabel() {
      return this.updatedAt ? moment(this.updatedAt).format("HH:mm:ss") : "--:--:--";
    },
  },
  watch: {
    // Com a aba em segundo plano, o número no título avisa que alguém precisa de atenção.
    "attention.length"(count) {
      document.title = count ? `(${count}) ${this.baseTitle}` : this.baseTitle;
    },
  },
  mounted() {
    this.baseTitle = document.title;
    this.load();
    this.tickTimer = setInterval(() => {
      this.now = serverNow();
    }, 1000);
    // O "ocioso" depende do tempo passar sem evento nenhum, então a lista
    // também é renovada de tempos em tempos, além dos eventos em tempo real.
    this.refreshTimer = setInterval(() => this.load(true), REFRESH_MS);
    this.watchEvents();
  },
  beforeDestroy() {
    document.title = this.baseTitle;
    clearInterval(this.tickTimer);
    clearInterval(this.refreshTimer);
    clearTimeout(this.reloadDebounce);
    this.unwatchEvents();
  },
  methods: {
    clock: formatClock,
    // Quem mais precisa de atenção sobe: pausa estourada/perigo, depois ocioso.
    severity(r) {
      return (r.overdue || r.danger.length ? 2 : 0) + (r.effectiveStatus === "idle" ? 1 : 0);
    },
    timeOnly(iso) {
      return iso ? moment(iso).format("HH:mm:ss") : "—";
    },
    dangerShown(row) {
      return this.expanded[row.user_id] ? row.danger : row.danger.slice(0, DANGER_PREVIEW);
    },
    toggleDanger(userId) {
      this.$set(this.expanded, userId, !this.expanded[userId]);
    },
    pausePercent(row) {
      const total = row.elapsed + Math.max(row.remaining, 0);
      return row.overdue || !total ? 100 : Math.min(100, Math.round((row.elapsed / total) * 100));
    },
    dateTime(iso) {
      return iso ? moment(iso).format("DD/MM/YYYY HH:mm:ss") : "—";
    },
    agoLabel(seconds) {
      return seconds < 5
        ? this.$t("supervision.now")
        : this.$t("supervision.ago", { time: formatSeconds(seconds) });
    },
    statusVariant(row) {
      if (row.overdue) return "danger";
      return { operating: "light-success", idle: "light-warning", paused: "light-info" }[row.effectiveStatus];
    },
    rowClass(row) {
      return {
        "supervision-row--overdue": row.overdue || row.danger.length > 0,
        "supervision-row--idle": row.effectiveStatus === "idle",
      };
    },
    load(silent = false) {
      return this.$http
        .get(`${this.$store.state.api}supervision/users`)
        .then((r) => {
          this.raw = r.data.data;
          this.updatedAt = serverNow();
        })
        .catch((e) => {
          if (!silent) {
            this.$message(this.$t("common.error"), this.apiErrorText(e, this.$t("supervision.loadError")), "error");
          }
        })
        .finally(() => {
          this.loading = false;
        });
    },
    async endPause(row) {
      const confirmed = await this.$bvModal.msgBoxConfirm(
        this.$t("supervision.endPauseConfirmText", { name: row.name, pause: row.pause.type_name }),
        {
          title: this.$t("supervision.endPauseConfirmTitle"),
          okTitle: this.$t("supervision.endPause"),
          cancelTitle: this.$t("common.cancel"),
          centered: true,
          size: "sm",
        }
      );
      if (!confirmed) return;

      this.ending = row.user_id;
      try {
        await this.$http.post(`${this.$store.state.api}supervision/users/${row.user_id}/end-pause`);
        this.$message(null, this.$t("supervision.endPauseDone"), "success");
        await this.load(true);
      } catch (e) {
        this.$message(this.$t("common.error"), this.apiErrorText(e), "error");
      } finally {
        this.ending = null;
      }
    },
    watchEvents() {
      if (typeof window.Echo === "undefined" || this.listener) return;

      const node = this.$store.state.selected_node;
      if (!node || !node.id) return;

      this.channelName = `supervision.${node.id}`;
      this.listener = (payload) => {
        // Interação de um usuário já listado: só renova a hora dele, sem recarregar a lista.
        if (payload && payload.kind === "activity" && payload.last_action_at) {
          const row = this.raw.find((r) => r.user_id === payload.user_id);
          if (row) {
            this.$set(row, "last_action_at", payload.last_action_at);
            return;
          }
        }
        clearTimeout(this.reloadDebounce);
        this.reloadDebounce = setTimeout(() => this.load(true), 400);
      };
      window.Echo.private(this.channelName).listen(EVENT, this.listener);
      this.watchConnection();
    },
    // Ao reconectar o websocket eventos podem ter sido perdidos: recarrega a lista.
    watchConnection() {
      const pusher = window.Echo.connector && window.Echo.connector.pusher;
      if (!pusher || !pusher.connection) return;

      this.connection = pusher.connection;
      this.onState = (states) => {
        this.live = states.current === "connected";
        if (states.current === "connected") this.load(true);
      };
      this.live = this.connection.state === "connected";
      this.connection.bind("state_change", this.onState);
    },
    unwatchEvents() {
      if (typeof window.Echo === "undefined" || !this.listener) return;

      if (this.connection && this.onState) this.connection.unbind("state_change", this.onState);
      window.Echo.private(this.channelName).stopListening(EVENT, this.listener);
      this.listener = null;
      this.channelName = null;
    },
  },
};
</script>

<style scoped>
.supervision-toolbar {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px 16px;
  padding: 1rem;
}

.supervision-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.chip {
  --chip: #283583;
  --chip-soft: rgba(40, 53, 131, 0.12);
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 0.45rem 0.8rem;
  border: 1px solid var(--chip);
  border-radius: 999px;
  background: transparent;
  color: inherit;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.15s, color 0.15s;
}

.chip--operating {
  --chip: #28c76f;
  --chip-soft: rgba(40, 199, 111, 0.16);
}

.chip--idle {
  --chip: #ff9f43;
  --chip-soft: rgba(255, 159, 67, 0.18);
}

.chip--paused {
  --chip: #00b8d4;
  --chip-soft: rgba(0, 184, 212, 0.16);
}

.chip--overdue {
  --chip: #ea5455;
  --chip-soft: rgba(234, 84, 85, 0.16);
}

.chip__dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--chip);
}

.chip__count {
  min-width: 1.5rem;
  padding: 0 0.4rem;
  border-radius: 999px;
  background: var(--chip-soft);
  color: var(--chip);
  font-size: 0.8rem;
  text-align: center;
  font-variant-numeric: tabular-nums;
}

.chip--active {
  background: var(--chip);
  color: #fff;
}

.chip--active .chip__dot {
  background: #fff;
}

.chip--active .chip__count {
  background: rgba(255, 255, 255, 0.25);
  color: #fff;
}

.supervision-search {
  position: relative;
  margin-left: auto;
  width: 300px;
  max-width: 100%;
}

.supervision-search__icon {
  position: absolute;
  top: 50%;
  left: 12px;
  transform: translateY(-50%);
  opacity: 0.55;
  pointer-events: none;
}

.supervision-search .form-control {
  padding-left: 36px;
}

.supervision-updated {
  white-space: nowrap;
}

.attention {
  margin: 0 1rem 1rem;
  padding: 0.75rem 1rem;
  border: 1px solid rgba(234, 84, 85, 0.35);
  border-radius: 0.5rem;
  background: rgba(234, 84, 85, 0.06);
}

.attention__title {
  display: flex;
  align-items: center;
  margin-bottom: 0.5rem;
  font-weight: 700;
  color: #ea5455;
}

.attention__list {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin: 0;
  padding: 0;
  list-style: none;
}

.attention__item {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 0.3rem 0.75rem;
  border-radius: 999px;
  cursor: pointer;
  font-size: 0.9rem;
}

.attention__item--danger {
  background: rgba(234, 84, 85, 0.14);
  color: #ea5455;
}

.attention__item--warning {
  background: rgba(255, 159, 67, 0.18);
  color: #e8750a;
}

.attention__dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: currentColor;
}

.attention__item--danger .attention__dot {
  animation: attention-pulse 1.4s ease-in-out infinite;
}

@keyframes attention-pulse {
  50% {
    box-shadow: 0 0 0 5px rgba(234, 84, 85, 0.3);
  }
}

@media (prefers-reduced-motion: reduce) {
  .attention__item--danger .attention__dot {
    animation: none;
  }
}

.supervision-table th {
  white-space: nowrap;
  font-size: 0.85rem;
}

.supervision-table td {
  font-size: 1.05rem;
}

/* O tema reduz <small> para ~80%; na supervisão o texto de apoio precisa continuar legível. */
.supervision-table td small {
  font-size: 0.92rem;
}

.supervision-table .badge {
  font-size: 0.95rem;
  padding: 0.35em 0.65em;
}

.supervision-table .supervision-pause__bar {
  height: 8px;
}

.supervision-updated {
  font-size: 0.9rem;
}

.chip {
  font-size: 1rem;
}

.attention__item {
  font-size: 1rem;
}

.attention__title {
  font-size: 1.05rem;
}

.supervision-row--overdue {
  background: rgba(234, 84, 85, 0.09);
  box-shadow: inset 4px 0 0 #ea5455;
}

.supervision-row--idle {
  background: rgba(255, 159, 67, 0.09);
  box-shadow: inset 4px 0 0 #ff9f43;
}

.tabular {
  font-variant-numeric: tabular-nums;
}
.live-dot {
  display: inline-block;
  width: 8px;
  height: 8px;
  margin-right: 4px;
  border-radius: 50%;
  background: #28c76f;
}

.live-dot--off {
  background: #ea5455;
}

.chip--danger {
  --chip: #ea5455;
  --chip-soft: rgba(234, 84, 85, 0.16);
}

.supervision-danger__more {
  display: inline-block;
  margin-top: 4px;
  font-size: 0.85rem;
}

.supervision-pause {
  min-width: 200px;
}

.supervision-pause__bar {
  height: 6px;
  margin: 4px 0;
  border-radius: 3px;
  background: rgba(0, 184, 212, 0.18);
  overflow: hidden;
}

.supervision-pause__bar span {
  display: block;
  height: 100%;
  background: #00b8d4;
  transition: width 1s linear;
}

.supervision-pause__bar--over {
  background: rgba(234, 84, 85, 0.2);
}

.supervision-pause__bar--over span {
  background: #ea5455;
}

.supervision-danger {
  display: flex;
  align-items: center;
  gap: 6px;
  white-space: nowrap;
}
.supervision-danger + .supervision-danger {
  margin-top: 4px;
}
</style>
